import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';

function fmt(h) {
  if (!h && h !== 0) return '–';
  const v = parseFloat(h);
  if (isNaN(v)) return '–';
  const hh = Math.floor(v);
  const mm = Math.round((v - hh) * 60);
  return `${hh}h ${mm.toString().padStart(2, '0')}m`;
}

function datumDe(d) {
  if (!d) return '–';
  return new Date(d).toLocaleDateString('de-DE');
}

export default function Zeiterfassung() {
  const { user } = useAuth() || {};
  const navigate = useNavigate();

  const [eintraege, setEintraege] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter
  const today = new Date();
  const defaultMonat = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const [monat, setMonat] = useState(defaultMonat);
  const [filterId, setFilterId] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') { navigate('/'); return; }
    api.get('/users').then(r => setUsers(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user, navigate]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (monat) params.set('monat', monat);
    if (filterId) params.set('user_id', filterId);
    api.get(`/zeiterfassung?${params}`)
      .then(r => setEintraege(Array.isArray(r.data) ? r.data : []))
      .catch(() => setEintraege([]))
      .finally(() => setLoading(false));
  }, [monat, filterId]);

  // Summen berechnen
  const gesamtStunden = eintraege.reduce((s, z) => s + (parseFloat(z.stunden) || 0), 0);

  // Gruppiert nach Mitarbeiter
  const nachMitarbeiter = eintraege.reduce((acc, z) => {
    const key = z.user_id;
    if (!acc[key]) acc[key] = { name: z.user_name || `User ${key}`, stunden: 0, tage: new Set() };
    acc[key].stunden += parseFloat(z.stunden) || 0;
    acc[key].tage.add(z.datum);
    return acc;
  }, {});

  if (user?.role !== 'admin') return null;

  return (
    <div style={{ padding: '0 0 100px' }}>
      <div className="page-header">
        <h1 className="page-title">Zeiterfassung</h1>
        <p className="page-subtitle">Übersicht aller Einträge</p>
      </div>

      {/* Filter */}
      <div style={{
        display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24,
        background: 'var(--bg-2)', borderRadius: 12, padding: 16,
        border: '1px solid var(--border)'
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 160px' }}>
          <label style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Monat</label>
          <input
            type="month"
            value={monat}
            onChange={e => setMonat(e.target.value)}
            style={{
              background: 'var(--bg-1)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '8px 12px', color: 'var(--text-1)',
              fontSize: 14, outline: 'none'
            }}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '1 1 200px' }}>
          <label style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>Mitarbeiter</label>
          <select
            value={filterId}
            onChange={e => setFilterId(e.target.value)}
            style={{
              background: 'var(--bg-1)', border: '1px solid var(--border)',
              borderRadius: 8, padding: '8px 12px', color: 'var(--text-1)',
              fontSize: 14, outline: 'none'
            }}
          >
            <option value="">Alle Mitarbeiter</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={() => { setMonat(defaultMonat); setFilterId(''); }}
            style={{
              background: 'transparent', border: '1px solid var(--border)',
              borderRadius: 8, padding: '8px 16px', color: 'var(--text-2)',
              fontSize: 13, cursor: 'pointer'
            }}
          >
            Zurücksetzen
          </button>
        </div>
      </div>

      {/* Summen-Karten */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <div style={{ background: 'var(--bg-2)', borderRadius: 12, padding: 16, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Einträge</div>
          <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--copper)' }}>{eintraege.length}</div>
        </div>
        <div style={{ background: 'var(--bg-2)', borderRadius: 12, padding: 16, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Gesamtstunden</div>
          <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--copper)' }}>{fmt(gesamtStunden)}</div>
        </div>
        <div style={{ background: 'var(--bg-2)', borderRadius: 12, padding: 16, border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Mitarbeiter</div>
          <div style={{ fontSize: 28, fontWeight: 600, color: 'var(--copper)' }}>{Object.keys(nachMitarbeiter).length}</div>
        </div>
      </div>

      {/* Mitarbeiter-Zusammenfassung */}
      {Object.keys(nachMitarbeiter).length > 0 && (
        <div style={{ background: 'var(--bg-2)', borderRadius: 12, padding: 16, border: '1px solid var(--border)', marginBottom: 24 }}>
          <div style={{ fontSize: 12, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 12 }}>Zusammenfassung nach Mitarbeiter</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {Object.values(nachMitarbeiter).sort((a, b) => b.stunden - a.stunden).map((m, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: 'var(--bg-1)', borderRadius: 8 }}>
                <div>
                  <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{m.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{m.tage.size} Arbeitstag{m.tage.size !== 1 ? 'e' : ''}</div>
                </div>
                <div style={{ fontWeight: 600, color: 'var(--copper)', fontSize: 16 }}>{fmt(m.stunden)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailtabelle */}
      <div style={{ background: 'var(--bg-2)', borderRadius: 12, border: '1px solid var(--border)', overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', fontSize: 12, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Alle Einträge
        </div>

        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>Lädt…</div>
        ) : eintraege.length === 0 ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--text-3)' }}>Keine Einträge für diesen Zeitraum</div>
        ) : (
          <>
            {/* Desktop Tabelle */}
            <div className="desktop-only" style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: 'var(--bg-1)' }}>
                    {['Datum', 'Mitarbeiter', 'Baustelle', 'Einstempel', 'Ausstempel', 'Stunden', 'Adresse'].map(h => (
                      <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 500, color: 'var(--text-2)', fontSize: 12, borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {eintraege.map((z, i) => (
                    <tr key={z.id} style={{ borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-1)' }}>
                      <td style={{ padding: '10px 12px', color: 'var(--text-1)', whiteSpace: 'nowrap' }}>{datumDe(z.datum)}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--text-1)' }}>{z.user_name || '–'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-2)' }}>{z.baustelle_name || '–'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{z.einstempel?.substring(0, 5) || '–'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{z.ausstempel?.substring(0, 5) || <span style={{ color: 'var(--copper)', fontSize: 12 }}>noch aktiv</span>}</td>
                      <td style={{ padding: '10px 12px', fontWeight: 600, color: 'var(--copper)', whiteSpace: 'nowrap' }}>{fmt(z.stunden)}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--text-3)', fontSize: 12 }}>{z.adresse_ein || '–'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Karten */}
            {/* Kein Inline-`display:`, sonst überschreibt es die `.mobile-only`
                Visibility-Regel — `.mobile-only` schaltet die Sichtbarkeit
                zwischen Desktop (none) und Mobile (block). */}
            <div className="mobile-only">
              {eintraege.map((z, i) => (
                <div key={z.id} style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'transparent' : 'var(--bg-1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div>
                      <div style={{ fontWeight: 500, color: 'var(--text-1)' }}>{z.user_name || '–'}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{datumDe(z.datum)}</div>
                    </div>
                    <div style={{ fontWeight: 600, color: 'var(--copper)', fontSize: 16 }}>{fmt(z.stunden)}</div>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 4 }}>
                    {z.baustelle_name && <span>🏗️ {z.baustelle_name} · </span>}
                    {z.einstempel?.substring(0, 5)} – {z.ausstempel?.substring(0, 5) || 'aktiv'}
                  </div>
                  {z.adresse_ein && <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 2 }}>📍 {z.adresse_ein}</div>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
